import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Snackbar } from '@mui/material';
import { reloadForNewBuild, watchForNewBuild } from 'c-lib';
import { useCommonTranslation } from 'c-translation';

export const BANNER_DELAY_MS = 30 * 60_000;

type Props = {
    // This tab's build. Only `vite build` sets it, so the check is off in dev and tests by default.
    buildId?: string;
    bannerDelayMs?: number;
};

/**
 * Moves an open tab onto a newer release. Once `/version.json` names a different build, the next
 * page change becomes a full page load: the user was leaving the page anyway, so nothing they were
 * editing is lost. A user who stays on one page gets a "Refresh page" banner after `bannerDelayMs`
 * instead of a reload mid-task. We release several times a day, so an instant reload is not an
 * option.
 */
function NewBuildReloader({
    buildId = import.meta.env.VITE_BUILD_ID,
    bannerDelayMs = BANNER_DELAY_MS,
}: Props) {
    const history = useHistory();
    const location = useLocation();
    const { pathname } = location;
    const locationRef = useRef(location);
    locationRef.current = location;
    const [newBuild, setNewBuild] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    // Read by the pathname effect, which must not re-run (and reload) when the build is first seen.
    const newBuildRef = useRef(false);
    const message = useCommonTranslation('Meta.newVersionAvailable');
    const refresh = useCommonTranslation('Meta.newVersionRefresh');

    useEffect(() => {
        if (buildId == null || buildId === '') return undefined;
        return watchForNewBuild({
            currentBuildId: buildId,
            onNewBuild: () => {
                newBuildRef.current = true;
                setNewBuild(true);
            },
        });
    }, [buildId]);

    // Pathname only: search params change with every filter, which is not leaving the page. The
    // target is the router's location (with its query), the page the user was heading to.
    useEffect(() => {
        if (newBuildRef.current) reloadForNewBuild(history.createHref(locationRef.current));
    }, [pathname, history]);

    useEffect(() => {
        if (!newBuild) return undefined;
        const timer = window.setTimeout(() => setShowBanner(true), bannerDelayMs);
        return () => window.clearTimeout(timer);
    }, [newBuild, bannerDelayMs]);

    return (
        <Snackbar
            open={showBanner}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message={message}
            action={
                <Button color="primary" size="small" onClick={() => reloadForNewBuild()}>
                    {refresh}
                </Button>
            }
        />
    );
}

export default NewBuildReloader;
